import React from 'react';
import { motion } from 'framer-motion';
import { Code, Target, ChevronRight } from 'lucide-react';

const SolutionCard: React.FC<{
  title: string;
  tagline: string;
  description: string;
  features: string[];
  links?: { label: string; href: string }[];
  icon: React.ElementType;
  imageSrc: string;
  imageAlt?: string;
}> = ({ title, tagline, description, features, links, icon: Icon, imageSrc, imageAlt }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="glass-card rounded-[2.5rem] overflow-hidden transition-theme flex flex-col h-full"
  >
    <div className="w-full aspect-[16/9] bg-[var(--accent-blue-soft)]/20 overflow-hidden border-b border-[var(--card-border)] relative">
      <img 
        src={imageSrc} 
        alt={imageAlt || title} 
        className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-500 relative z-10"
        onError={(e) => {
          (e.target as HTMLImageElement).style.opacity = '0';
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)] text-sm italic p-4 text-center z-0">
          
      </div>
    </div>
    <div className="p-10 flex flex-col flex-grow">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 bg-[var(--accent-blue-soft)] rounded-xl flex items-center justify-center text-[var(--accent-blue)] transition-theme">
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-[var(--text-main)] transition-theme">{title}</h3>
          <p className="text-xs text-[var(--accent-blue)] font-bold uppercase tracking-wider transition-theme">{tagline}</p>
        </div>
      </div>
      <p className="text-sm text-[var(--text-muted)] mb-8 leading-relaxed transition-theme">{description}</p>
      
      <div className="flex-grow">
        <h4 className="font-bold text-[var(--text-main)] text-sm mb-4 transition-theme uppercase tracking-tight">Key highlights</h4>
        <ul className="space-y-3 mb-10">
          {features.map((f, i) => (
            <li key={i} className="flex items-center gap-3 text-[var(--text-muted)] text-sm transition-theme">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent-blue)]" />
              {f}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap gap-4 pt-6 border-t border-[var(--card-border)]">
        {links?.map((l, i) => (
          <a key={i} href={l.href} className="text-sm font-bold text-[var(--accent-blue)] hover:brightness-75 flex items-center gap-1 group transition-all">
            {l.label} <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        ))}
      </div>
    </div>
  </motion.div>
);

const Solutions: React.FC = () => {
  return (
    <div className="container mx-auto px-6 max-w-6xl space-y-32 pt-32">
      <div className="text-center">
        <h1 className="text-5xl font-bold mb-6 text-[var(--text-main)] transition-theme">Our Products</h1>
        {/* <p className="text-[var(--text-muted)] max-w-2xl mx-auto transition-theme">
          Precision hardware and simulation software ecosystem supporting 
          the next generation of quantum engineering and diagnostics.
        </p> */}
      </div>

      <div id="products" className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <SolutionCard 
          title="OptiCore"
          tagline="Open-source optical simulation software"
          icon={Code}
          imageSrc="/images/software_preciqube.jpg"
          imageAlt="OptiCore Interface"
          description="A GUI-based, frequency-domain optical simulation tool built on Finesse3 for modelling optical cavities and interferometric systems. It provides a clean interface for iterating complex configurations."
          features={[
            "Open-source and community-driven",
            "GUI-based interface for faster iteration",
            "Frequency-domain simulations for optical cavities",
            "Suitable for research and early-stage design"
          ]}
          links={[
          ]}
        />

        <SolutionCard 
          title="Aligner"
          tagline="Precision laser and optics alignment tool"
          icon={Target}
          imageSrc="/images/Aligner.png"
          imageAlt="Aligner Hardware"
          description="A compact, modular alignment device designed to improve precision and reduce parallax error. It simplifies common optical alignment tasks performed manually."
          features={[
            "Adjustable optical path length",
            "Precise height adjustment",
            "Removable mirror/lens alignment module",
            "Gear-based angular adjustment with locking",
            "Provisional patent granted"
          ]}
          links={[

          ]}
        />
      </div>
      {/* { label: "User Guide", href: "#" } */}

      <section id="autoalign" className="glass-card rounded-[3rem] p-12 overflow-hidden transition-theme">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <div className="lg:w-1/2">
            <span className="text-[var(--accent-blue)] font-bold text-sm uppercase mb-4 block transition-theme">In Development</span>
            <h2 className="text-4xl font-bold mb-6 text-[var(--text-main)] transition-theme">Turnkey AutoAlign Optical Cavity</h2>
            <p className="text-[var(--text-muted)] mb-8 leading-relaxed transition-theme">
              An in-development, auto-aligned optical cavity platform designed for stability, 
              repeatability, and minimal manual intervention.
            </p>
            <div className="bg-[var(--bg-main)]/50 p-6 rounded-2xl border border-[var(--card-border)] transition-theme">
              <h4 className="font-bold mb-4 text-[var(--text-main)]">Current Status</h4>
              <ul className="space-y-3 text-sm text-[var(--text-muted)]">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[var(--accent-blue)] animate-pulse" />
                  Currently in manufacturing phase
                </li>
                {/* <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[var(--text-muted)] opacity-50" />
                  Details to be added as the platform matures
                </li> */}
              </ul>
            </div>
          </div>
          <div className="lg:w-1/2 w-full">
            <div className="aspect-video bg-[var(--accent-blue-soft)]/20 rounded-[2rem] overflow-hidden border border-[var(--card-border)] transition-theme relative">
              <img 
                src="/images/Optical_cavity.png" 
                alt="AutoAlign Prototype" 
                className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity relative z-10" 
                onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)] text-sm italic text-center z-0">
                 
              </div>
            </div>
            <p className="mt-4 text-[10px] text-[var(--text-muted)] italic text-center opacity-70">
              Note: The image above is for reference only and does not represent the real application or final product.
            </p>
          </div>
        </div>
      </section>

      {/* <div id="future" className="bg-[var(--accent-blue-soft)]/20 rounded-[3rem] p-12 text-center border border-[var(--accent-blue)]/20 backdrop-blur transition-theme">
        <h3 className="text-2xl font-bold text-[var(--text-main)] mb-4 transition-theme">Looking Ahead</h3>
        <p className="text-[var(--text-muted)] max-w-2xl mx-auto mb-8 transition-theme text-sm">
          These products form the technical foundation for PreciQube's 
          long-term vision in quantum-enhanced diagnostic systems.
        </p>
        <div className="flex justify-center gap-12 flex-wrap">
          <div className="text-center">
            <p className="text-[var(--accent-blue)] font-bold mb-1 transition-theme">Precision</p>
            <p className="text-[var(--text-muted)] text-[9px] uppercase tracking-widest font-bold transition-theme">and reliability</p>
          </div>
          <div className="text-center">
            <p className="text-[var(--accent-blue)] font-bold mb-1 transition-theme">Scalability</p>
            <p className="text-[var(--text-muted)] text-[9px] uppercase tracking-widest font-bold transition-theme">and reproducibility</p>
          </div>
          <div className="text-center">
            <p className="text-[var(--accent-blue)] font-bold mb-1 transition-theme">Strong IP</p>
            <p className="text-[var(--text-muted)] text-[9px] uppercase tracking-widest font-bold transition-theme">Foundations</p>
          </div>
        </div> */}
      {/* </div> */}
    </div>
  );
};

export default Solutions;